import { useEffect, useState } from "react";
import { History, UserRound, Activity, Hospital } from "lucide-react";
import api from "@/api";
import { StatusBadge } from "@/components/shared/StatusBadge";

const DOSSIER_LABEL = { actif: "Suivi actif", hospitalise: "Hospitalisé", suspendu: "Suspendu", termine: "Terminé" };

function fmt(d) {
  return d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "";
}

// Historique append-only des statuts (clinique + administratif) — toutes les évaluations restent consultables.
export default function StatusHistoryCard({ patientId, refreshKey }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/patients/${patientId}/status-history`)
      .then((r) => { if (!cancelled) setRows(r.data?.history || []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [patientId, refreshKey]);

  return (
    <div className="dash-card" data-testid="status-history-card">
      <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <History className="w-5 h-5 text-emerald-600" />
        Historique des statuts
      </h3>
      {rows === null ? (
        <p className="text-sm text-slate-400">Chargement…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="status-history-empty">Aucun changement de statut enregistré</p>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="status-history-list">
          {rows.map((h) => (
            <li key={h.id} className="py-3 flex items-start gap-3" data-testid="status-history-item">
              <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${h.kind === "dossier" ? "bg-slate-100 text-slate-600" : h.source === "manual" ? "bg-sky-50 text-sky-700" : "bg-emerald-50 text-emerald-700"}`}>
                {h.kind === "dossier" ? <Hospital className="w-4 h-4" /> : h.source === "manual" ? <UserRound className="w-4 h-4" /> : <Activity className="w-4 h-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {h.kind === "dossier" ? (
                    <span className="font-semibold text-slate-800">Dossier : {DOSSIER_LABEL[h.previousStatus] || h.previousStatus} → {DOSSIER_LABEL[h.status] || h.status}</span>
                  ) : (
                    <>
                      <StatusBadge status={h.previousStatus} size="sm" />
                      <span className="text-slate-400">→</span>
                      <StatusBadge status={h.status} size="sm" />
                    </>
                  )}
                  <span className="text-xs text-slate-500 ml-auto">{fmt(h.createdAt)}</span>
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  {h.source === "manual"
                    ? `Correction par ${h.actorName || "un soignant"}${h.actorRole ? ` (${h.actorRole})` : ""}${h.reason ? ` — ${h.reason}` : ""}`
                    : `Calculé automatiquement depuis le check-in (niveau ${h.checkinAlertLevel || "—"})`}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
